/**
 * Rax Persona Export/Import
 *
 * Export personas as .persona.yaml files and import shared personas.
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxApi, type Persona, type PersonaExport } from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";

@customElement("rax-persona-export")
export class RaxPersonaExportComponent extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.7);
        backdrop-filter: blur(4px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
      }
      
      .panel {
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-xl);
        width: 100%;
        max-width: 480px;
        padding: var(--space-5);
      }
      
      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-5);
      }
      
      .title {
        font-size: var(--font-xl);
        font-weight: 600;
        color: var(--text-primary);
      }
      
      .close-btn {
        background: transparent;
        border: none;
        color: var(--text-tertiary);
        font-size: 1.5rem;
        cursor: pointer;
      }
      
      .tabs {
        display: flex;
        gap: var(--space-2);
        margin-bottom: var(--space-5);
      }
      
      .tab {
        flex: 1;
        background: var(--bg-elevated);
        border: 2px solid var(--border-color);
        border-radius: var(--radius-md);
        padding: var(--space-3);
        font-size: var(--font-md);
        font-weight: 500;
        color: var(--text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .tab:hover {
        border-color: var(--text-tertiary);
      }
      
      .tab.active {
        border-color: var(--rax-yellow);
        background: rgba(255, 208, 0, 0.1);
        color: var(--text-primary);
      }
      
      .content {
        min-height: 200px;
      }
      
      .export-section {
        text-align: center;
      }
      
      .export-preview {
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        padding: var(--space-4);
        margin-bottom: var(--space-4);
        text-align: left;
      }
      
      .preview-name {
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: var(--space-2);
      }
      
      .preview-meta {
        font-size: var(--font-sm);
        color: var(--text-tertiary);
      }
      
      .export-code {
        background: var(--bg-subtle);
        border-radius: var(--radius-md);
        padding: var(--space-3);
        font-family: monospace;
        font-size: var(--font-sm);
        color: var(--text-secondary);
        max-height: 200px;
        overflow-y: auto;
        white-space: pre-wrap;
        word-break: break-all;
        margin-bottom: var(--space-4);
      }
      
      .export-btn {
        background: var(--rax-yellow);
        border: none;
        border-radius: var(--radius-md);
        padding: var(--space-3) var(--space-5);
        font-size: var(--font-md);
        font-weight: 600;
        color: #000;
        cursor: pointer;
      }
      
      .export-btn:hover {
        background: var(--rax-yellow-hover);
      }
      
      .import-section {
        text-align: center;
      }
      
      .drop-zone {
        border: 2px dashed var(--border-color);
        border-radius: var(--radius-lg);
        padding: var(--space-8);
        text-align: center;
        cursor: pointer;
        transition: all var(--transition-fast);
        margin-bottom: var(--space-4);
      }
      
      .drop-zone:hover,
      .drop-zone.dragover {
        border-color: var(--rax-yellow);
        background: rgba(255, 208, 0, 0.05);
      }
      
      .drop-icon {
        font-size: 3rem;
        margin-bottom: var(--space-3);
        opacity: 0.5;
      }
      
      .drop-text {
        font-size: var(--font-md);
        color: var(--text-secondary);
        margin-bottom: var(--space-2);
      }
      
      .drop-hint {
        font-size: var(--font-sm);
        color: var(--text-tertiary);
      }
      
      .import-input {
        display: none;
      }
      
      .paste-section {
        margin-top: var(--space-4);
      }
      
      .paste-label {
        font-size: var(--font-sm);
        color: var(--text-tertiary);
        margin-bottom: var(--space-2);
      }
      
      .paste-input {
        width: 100%;
        min-height: 100px;
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        padding: var(--space-3);
        font-family: monospace;
        font-size: var(--font-sm);
        color: var(--text-primary);
        resize: vertical;
      }
      
      .paste-input:focus {
        outline: none;
        border-color: var(--rax-yellow);
      }
      
      .import-btn {
        margin-top: var(--space-4);
        background: var(--rax-yellow);
        border: none;
        border-radius: var(--radius-md);
        padding: var(--space-3) var(--space-5);
        font-size: var(--font-md);
        font-weight: 600;
        color: #000;
        cursor: pointer;
      }
      
      .import-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      .success-msg {
        color: #10b981;
        font-size: var(--font-sm);
        margin-top: var(--space-3);
      }
      
      .error-msg {
        color: var(--error);
        font-size: var(--font-sm);
        margin-top: var(--space-3);
      }
    `,
  ];

  @property({ type: Object })
  persona: Persona | null = null;

  @property({ type: Boolean })
  open = false;

  @state() private activeTab: "export" | "import" = "export";
  @state() private exportData: PersonaExport | null = null;
  @state() private importText = "";
  @state() private loading = false;
  @state() private success = "";
  @state() private error = "";
  @state() private dragover = false;

  override updated(changed: Map<string, unknown>) {
    if (changed.has("persona") && this.persona) {
      this.loadExportData();
    }
    if (changed.has("open") && this.open) {
      this.activeTab = this.persona ? "export" : "import";
      this.success = "";
      this.error = "";
      this.importText = "";
    }
  }

  private async loadExportData() {
    if (!this.persona) return;

    try {
      const response = await raxApi.exportPersona(this.persona.id);
      if (response.success && response.data) {
        this.exportData = response.data;
      }
    } catch (err) {
      console.error("[PersonaExport] Error loading export:", err);
    }
  }

  private handleDownload() {
    if (!this.exportData || !this.persona) return;

    const yaml = this.toYaml(this.exportData);
    const blob = new Blob([yaml], { type: "text/yaml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${this.persona.name.toLowerCase().replace(/\s+/g, "-")}.persona.yaml`;
    a.click();
    URL.revokeObjectURL(url);

    this.success = "Downloaded!";
    setTimeout(() => (this.success = ""), 2000);
  }

  private handleCopy() {
    if (!this.exportData) return;

    const yaml = this.toYaml(this.exportData);
    navigator.clipboard.writeText(yaml);
    this.success = "Copied to clipboard!";
    setTimeout(() => (this.success = ""), 2000);
  }

  private toYaml(data: PersonaExport): string {
    // Simple YAML serialization
    const lines: string[] = [
      `# Rax Persona Export`,
      `# Created: ${new Date().toISOString()}`,
      ``,
      `name: "${data.name}"`,
      `role: ${data.role}`,
      data.description ? `description: "${data.description}"` : "",
      data.author ? `author: "${data.author}"` : "",
      data.version ? `version: "${data.version}"` : "",
      ``,
      `appearance:`,
      `  hairColor: "${data.appearance.hairColor}"`,
      `  hairStyle: ${data.appearance.hairStyle}`,
      `  outfit: ${data.appearance.outfit}`,
      `  skinTone: ${data.appearance.skinTone}`,
      ``,
      `personality:`,
      `  style: "${data.personality.style}"`,
      `  voice: "${data.personality.voice}"`,
      ``,
      `skills:`,
      `  enabled: [${data.skills.enabled.join(", ")}]`,
      `  disabled: [${data.skills.disabled.join(", ")}]`,
      ``,
      `preferences:`,
      `  autonomy: ${data.preferences.autonomy}`,
      data.preferences.model ? `  model: ${data.preferences.model}` : "",
    ];

    if (data.systemPromptAdditions) {
      lines.push(``, `systemPromptAdditions: |`);
      data.systemPromptAdditions.split("\n").forEach((line) => {
        lines.push(`  ${line}`);
      });
    }

    return lines.filter((l) => l !== "").join("\n");
  }

  private handleDrop(e: DragEvent) {
    e.preventDefault();
    this.dragover = false;

    const file = e.dataTransfer?.files[0];
    if (file) {
      this.readFile(file);
    }
  }

  private handleFileSelect(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.readFile(file);
    }
  }

  private readFile(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      this.importText = reader.result as string;
    };
    reader.readAsText(file);
  }

  private async handleImport() {
    if (!this.importText.trim()) return;

    this.loading = true;
    this.error = "";
    this.success = "";

    try {
      // Parse YAML (simple parser for our format)
      const data = this.parseYaml(this.importText);
      const response = await raxApi.importPersona(data);

      if (response.success && response.data) {
        this.success = `Imported ${response.data.name}!`;
        this.dispatchEvent(
          new CustomEvent("persona-imported", {
            detail: { persona: response.data },
            bubbles: true,
            composed: true,
          }),
        );
        setTimeout(() => this.handleClose(), 1500);
      } else {
        this.error = response.error ?? "Failed to import persona";
      }
    } catch (err) {
      console.error("[PersonaExport] Error importing:", err);
      this.error = err instanceof Error ? err.message : "Failed to parse persona file";
    } finally {
      this.loading = false;
    }
  }

  private parseYaml(text: string): PersonaExport {
    // Simple YAML parser for our specific format
    const lines = text.split("\n");
    const result: Record<string, unknown> = {};
    let currentSection = "";
    let systemPromptLines: string[] = [];
    let inSystemPrompt = false;

    for (const line of lines) {
      if (line.startsWith("#") || line.trim() === "") continue;

      if (inSystemPrompt) {
        if (line.startsWith("  ")) {
          systemPromptLines.push(line.slice(2));
        } else {
          inSystemPrompt = false;
        }
      }

      const match = line.match(/^(\w+):\s*(.*)$/);
      if (match) {
        const [, key, value] = match;
        if (value === "" || value === "|") {
          currentSection = key;
          if (key === "systemPromptAdditions") {
            inSystemPrompt = true;
          }
          if (!result[key]) result[key] = {};
        } else {
          const cleanValue = value.replace(/^["']|["']$/g, "");
          if (currentSection) {
            (result[currentSection] as Record<string, unknown>)[key] = this.parseValue(cleanValue);
          } else {
            result[key] = this.parseValue(cleanValue);
          }
        }
      }

      const subMatch = line.match(/^\s+(\w+):\s*(.*)$/);
      if (subMatch && currentSection) {
        const [, key, value] = subMatch;
        const cleanValue = value.replace(/^["']|["']$/g, "");
        (result[currentSection] as Record<string, unknown>)[key] = this.parseValue(cleanValue);
      }
    }

    if (systemPromptLines.length > 0) {
      result.systemPromptAdditions = systemPromptLines.join("\n");
    }

    return result as unknown as PersonaExport;
  }

  private parseValue(value: string): unknown {
    if (value.startsWith("[") && value.endsWith("]")) {
      return value
        .slice(1, -1)
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s);
    }
    if (value === "true") return true;
    if (value === "false") return false;
    if (/^\d+$/.test(value)) return parseInt(value, 10);
    return value;
  }

  private handleClose() {
    this.dispatchEvent(new CustomEvent("close", { bubbles: true, composed: true }));
  }

  override render() {
    if (!this.open) return nothing;

    return html`
      <div class="overlay" @click=${(e: Event) => e.target === e.currentTarget && this.handleClose()}>
        <div class="panel">
          <div class="header">
            <span class="title">Persona Export/Import</span>
            <button class="close-btn" @click=${this.handleClose}>&times;</button>
          </div>

          <div class="tabs">
            <button class="tab ${this.activeTab === "export" ? "active" : ""}" @click=${() => (this.activeTab = "export")}>
              Export
            </button>
            <button class="tab ${this.activeTab === "import" ? "active" : ""}" @click=${() => (this.activeTab = "import")}>
              Import
            </button>
          </div>

          <div class="content">
            ${this.activeTab === "export" ? this.renderExport() : this.renderImport()}
          </div>

          ${this.success ? html`<div class="success-msg">${this.success}</div>` : nothing}
          ${this.error ? html`<div class="error-msg">${this.error}</div>` : nothing}
        </div>
      </div>
    `;
  }

  private renderExport() {
    if (!this.persona || !this.exportData) {
      return html`
        <div class="export-section">Select a persona to export</div>
      `;
    }

    return html`
      <div class="export-section">
        <div class="export-preview">
          <div class="preview-name">${this.persona.name}</div>
          <div class="preview-meta">${this.persona.role} - ${this.persona.skills.enabled.length} skills</div>
        </div>

        <div class="export-code">${this.toYaml(this.exportData)}</div>

        <button class="export-btn" @click=${this.handleDownload} style="margin-right: var(--space-2)">
          Download .persona.yaml
        </button>
        <button class="export-btn" @click=${this.handleCopy} style="background: var(--bg-elevated); color: var(--text-primary);">
          Copy
        </button>
      </div>
    `;
  }

  private renderImport() {
    return html`
      <div class="import-section">
        <div
          class="drop-zone ${this.dragover ? "dragover" : ""}"
          @click=${() => this.shadowRoot?.querySelector<HTMLInputElement>(".import-input")?.click()}
          @dragover=${(e: DragEvent) => {
            e.preventDefault();
            this.dragover = true;
          }}
          @dragleave=${() => (this.dragover = false)}
          @drop=${this.handleDrop}
        >
          <div class="drop-icon">📄</div>
          <div class="drop-text">Drop .persona.yaml file here</div>
          <div class="drop-hint">or click to browse</div>
        </div>

        <input class="import-input" type="file" accept=".yaml,.yml" @change=${this.handleFileSelect} />

        <div class="paste-section">
          <div class="paste-label">Or paste YAML content:</div>
          <textarea
            class="paste-input"
            placeholder="Paste persona YAML here..."
            .value=${this.importText}
            @input=${(e: Event) => (this.importText = (e.target as HTMLTextAreaElement).value)}
          ></textarea>
        </div>

        <button class="import-btn" ?disabled=${!this.importText.trim() || this.loading} @click=${this.handleImport}>
          ${this.loading ? "Importing..." : "Import Persona"}
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-persona-export": RaxPersonaExportComponent;
  }
}
